import type { CreateFormData } from "@/components/create/types";

export type FrontDesignField =
  | "color"
  | "length"
  | "neckline"
  | "silhouette"
  | "sleeve"
  | "fabric";

export interface FrontDesignReadiness {
  readyToGenerate: boolean;
  concreteFieldCount: number;
  promptSignalCount: number;
  promptSignals: string[];
  missingFields: FrontDesignField[];
  briefConfidence: DesignBriefConfidence;
  reason: string;
}

export type DesignBriefDimension =
  | "occasion"
  | "visualDirection"
  | "comfortConstraints"
  | "dislikes"
  | "movementNeeds"
  | "coverageLevel"
  | "priorityTradeoffs";

export interface DesignBriefDimensionStatus {
  captured: boolean;
  evidence: string[];
}

export interface DesignBriefConfidence {
  score: number;
  capturedDimensions: DesignBriefDimension[];
  missingDimensions: DesignBriefDimension[];
  weakestDimension: DesignBriefDimension | null;
  dimensions: Record<DesignBriefDimension, DesignBriefDimensionStatus>;
  guidance: string;
}

export interface DesignBriefConfidenceOptions {
  /**
   * Optional user-authored context outside formState.prompt, such as recent user
   * messages. Avoid assistant text here or the assistant's own questions may
   * look like answered brief dimensions.
   */
  contextText?: string;
}

export const FRONT_DESIGN_FIELDS: FrontDesignField[] = [
  "color",
  "length",
  "neckline",
  "silhouette",
  "sleeve",
  "fabric",
];

export const DESIGN_BRIEF_DIMENSIONS: DesignBriefDimension[] = [
  "occasion",
  "visualDirection",
  "comfortConstraints",
  "dislikes",
  "movementNeeds",
  "coverageLevel",
  "priorityTradeoffs",
];

const BRIEF_DIMENSION_GUIDANCE: Record<DesignBriefDimension, string> = {
  occasion: "Ask what the dress is for and how formal it should feel.",
  visualDirection:
    "Offer cohesive visual directions or ask what feeling and impression the dress should create.",
  comfortConstraints:
    "Ask for comfort boundaries: tightness, support, weight, itchiness, heat, neckline depth, and shapewear tolerance.",
  dislikes:
    "Ask what would make the user reject a design immediately, then preserve those negative constraints.",
  movementNeeds:
    "Ask whether they need to dance, sit, walk outdoors, climb stairs, hug people, take photos, or wear it for many hours.",
  coverageLevel:
    "Ask where they want coverage or support and what can feel more revealing.",
  priorityTradeoffs:
    "Ask which matters most: comfort, drama, coverage, price, uniqueness, tradition, or understatement.",
};

const PROMPT_SIGNAL_PATTERNS: Array<{
  signal: string;
  pattern: RegExp;
}> = [
  {
    signal: "color",
    pattern:
      /\b(ivory|white|off[-\s]?white|cream|champagne|blush|pink|red|burgundy|wine|black|navy|blue|emerald|green|sage|gold|silver|lavender|purple|pearl)\b/i,
  },
  {
    signal: "silhouette",
    pattern:
      /\b(a[-\s]?line|ball\s*gown|princess|mermaid|trumpet|sheath|column|fit[-\s]?and[-\s]?flare|empire|fitted|flowy|loose\s+fit|relaxed\s+fit|roomy|danceable)\b/i,
  },
  {
    signal: "neckline",
    pattern:
      /\b(sweetheart|v[-\s]?neck|deep\s*v|off[-\s]?shoulder|strapless|halter|scoop|bateau|boat\s*neck|square\s*neck|high\s*neck|one[-\s]?shoulder)\b/i,
  },
  {
    signal: "sleeve",
    pattern:
      /\b(sleeveless|long\s*sleeve|short\s*sleeve|cap\s*sleeve|puff\s*sleeve|bell\s*sleeve|bishop\s*sleeve|spaghetti\s*strap|straps?|detachable\s*sleeves?)\b/i,
  },
  {
    signal: "fabric",
    pattern:
      /\b(satin|lace|tulle|chiffon|crepe|crepe\s*de\s*chine|organza|velvet|mikado|silk|charmeuse|taffeta|jacquard)\b/i,
  },
  {
    signal: "length",
    pattern:
      /\b(floor[-\s]?length|tea[-\s]?length|mini|midi|knee[-\s]?length|ankle[-\s]?length|cathedral|chapel|train)\b/i,
  },
  {
    signal: "detail",
    pattern:
      /\b(corset|boned|beaded|beading|embroidery|embroidered|applique|appliqué|floral|slit|pleated|draped|ruffle|ruffled|overskirt|bow|danc(?:e|ing)|movement|mobility|comfortable)\b/i,
  },
];

function hasFieldValue(value: unknown): boolean {
  if (Array.isArray(value)) {
    return value.some(hasFieldValue);
  }
  return typeof value === "string" ? value.trim().length > 0 : Boolean(value);
}

function getPromptSignals(prompt: string | undefined): string[] {
  if (!prompt?.trim()) return [];

  const signals: string[] = [];
  for (const { signal, pattern } of PROMPT_SIGNAL_PATTERNS) {
    if (pattern.test(prompt)) {
      signals.push(signal);
    }
  }
  return signals;
}

/**
 * Which design attributes a freeform message actually names (color,
 * silhouette, neckline, sleeve, fabric, length, detail). Exported so callers
 * asking "did the customer name a concrete attribute?" — the planner's
 * design-recovery gate — read the SAME vocabulary readiness is scored against,
 * instead of carrying a second, drifting word list.
 */
export function getDesignVocabSignals(text: string | undefined): string[] {
  return getPromptSignals(text);
}

function valueToBriefText(value: unknown): string {
  if (Array.isArray(value)) {
    return value.map(valueToBriefText).filter(Boolean).join(" ");
  }
  return typeof value === "string" ? value : "";
}

function buildBriefText(
  formState: Partial<CreateFormData>,
  contextText?: string
): string {
  const parts = [
    formState.preset_type,
    formState.modestyPreference,
    formState.prompt,
    formState.color,
    formState.length,
    formState.sleeve,
    formState.neckline,
    formState.silhouette,
    formState.fabric,
    contextText,
  ];

  return parts.map(valueToBriefText).filter(Boolean).join(" ").toLowerCase();
}

function testAny(text: string, patterns: RegExp[]): boolean {
  return patterns.some(pattern => pattern.test(text));
}

function createEmptyBriefDimensions(): Record<
  DesignBriefDimension,
  DesignBriefDimensionStatus
> {
  return DESIGN_BRIEF_DIMENSIONS.reduce(
    (acc, dimension) => {
      acc[dimension] = { captured: false, evidence: [] };
      return acc;
    },
    {} as Record<DesignBriefDimension, DesignBriefDimensionStatus>
  );
}

function captureDimension(
  dimensions: Record<DesignBriefDimension, DesignBriefDimensionStatus>,
  dimension: DesignBriefDimension,
  evidence: string
): void {
  dimensions[dimension].captured = true;
  if (!dimensions[dimension].evidence.includes(evidence)) {
    dimensions[dimension].evidence.push(evidence);
  }
}

export function evaluateDesignBriefConfidence(
  formState: Partial<CreateFormData>,
  options: DesignBriefConfidenceOptions = {}
): DesignBriefConfidence {
  const briefText = buildBriefText(formState, options.contextText);
  const dimensions = createEmptyBriefDimensions();
  const concreteFields = FRONT_DESIGN_FIELDS.filter(field =>
    hasFieldValue(formState[field])
  );
  const promptSignals = getPromptSignals(formState.prompt);

  if (
    hasFieldValue(formState.preset_type) &&
    !/^(custom|upload)$/i.test(String(formState.preset_type))
  ) {
    captureDimension(dimensions, "occasion", "occasion preset");
  }
  if (
    testAny(briefText, [
      /\b(wedding|bridal|bride|ceremony|reception|courthouse|elopement)\b/i,
      /\b(wedding guest|bridesmaid|mother of the bride|mother of the groom)\b/i,
      /\b(prom|homecoming|quincea(?:ñ|n)era|gala|formal|black tie|cocktail)\b/i,
      /\b(church|garden|beach|ballroom|venue|indoors?|outdoors?)\b/i,
    ])
  ) {
    captureDimension(dimensions, "occasion", "occasion or environment");
  }

  if (concreteFields.length >= 2) {
    captureDimension(dimensions, "visualDirection", "structured design fields");
  }
  if (promptSignals.length >= 3) {
    captureDimension(dimensions, "visualDirection", "rich visual prompt");
  }
  if (concreteFields.length >= 1 && promptSignals.length >= 2) {
    captureDimension(dimensions, "visualDirection", "directional prompt");
  }
  if (
    testAny(briefText, [
      /\b(confident|romantic|powerful|soft|elegant|sexy|modest|effortless|glamorous)\b/i,
      /\b(classic|timeless|modern|minimal(?:ist)?|dramatic|statement|boho|ethereal)\b/i,
      /\b(structured|flowy|loose fit|relaxed fit|tailored|architectural|sleek|princess|clean line)\b/i,
      /\b(crisp|drapey|airy|light|matte|shiny|textured|stretchy|opaque)\b/i,
    ])
  ) {
    captureDimension(
      dimensions,
      "visualDirection",
      "style or feeling language"
    );
  }

  if (
    testAny(briefText, [
      /\b(comfort|comfortable|easy to wear|not restrictive|restrictive)\b/i,
      /\b(tight|not tight|avoid tight|loose fit|roomy|doesn'?t cling|do not cling|clingy)\b/i,
      /\b(itchy|scratchy|heavy|lightweight|hot|heat|breathable|cool)\b/i,
      /\b(bra|support|supportive|shapewear|corset tolerance|boning)\b/i,
    ])
  ) {
    captureDimension(dimensions, "comfortConstraints", "comfort boundary");
  }

  if (
    testAny(briefText, [
      /\b(no|not|avoid|without|exclude|remove)\b.{0,50}\b(strapless|mermaid|black|gold|sparkle|sequins?|lace|tight|heavy|itchy|revealing|plunge|slit|corset|princess|white|train)\b/i,
      /\b(hate|dislike|don'?t like|do not like|not into|reject|absolutely not)\b/i,
      /\b(should not|must not|nothing too|not too)\b/i,
    ])
  ) {
    captureDimension(dimensions, "dislikes", "negative constraint");
  }

  if (
    testAny(briefText, [
      /\b(dance|dancing|sit|sitting|stairs?|walk|walking|stride|move|mobility)\b/i,
      /\b(outdoor|outside|garden|beach|grass|cobblestone)\b/i,
      /\b(hug|photos?|ceremony|reception|long wear|all day|all night|many hours)\b/i,
    ])
  ) {
    captureDimension(dimensions, "movementNeeds", "movement or timeline need");
  }

  if (hasFieldValue(formState.modestyPreference)) {
    captureDimension(dimensions, "coverageLevel", "modesty preference");
  }
  if (
    testAny(briefText, [
      /\b(modest|modesty|coverage|covered|conservative|not revealing|balanced coverage)\b/i,
      /\b(revealing|sexy|neckline depth|deep v|plunge|strapless|open back|low back)\b/i,
      /\b(sleeves?|sleeveless|arms?|chest|cleavage|legs?|slit|high neck)\b/i,
    ])
  ) {
    captureDimension(dimensions, "coverageLevel", "coverage language");
  }

  if (
    testAny(briefText, [
      /\b(priority|priorities|matters most|most important|care most|main thing)\b/i,
      /\b(comfort|coverage|drama|price|budget|unique|uniqueness|tradition|traditional)\b.{0,25}\b(over|more than|matters|first)\b/i,
      /\b(overdressed|understated|not too formal|not too casual|formality)\b/i,
      /\b(comfortable but|dramatic but|modest but|unique but|traditional but)\b/i,
    ])
  ) {
    captureDimension(dimensions, "priorityTradeoffs", "priority or tradeoff");
  }

  const capturedDimensions = DESIGN_BRIEF_DIMENSIONS.filter(
    dimension => dimensions[dimension].captured
  );
  const missingDimensions = DESIGN_BRIEF_DIMENSIONS.filter(
    dimension => !dimensions[dimension].captured
  );
  const weakestDimension = missingDimensions[0] ?? null;
  const score = Number(
    (capturedDimensions.length / DESIGN_BRIEF_DIMENSIONS.length).toFixed(2)
  );

  return {
    score,
    capturedDimensions,
    missingDimensions,
    weakestDimension,
    dimensions,
    guidance: weakestDimension
      ? BRIEF_DIMENSION_GUIDANCE[weakestDimension]
      : "The brief covers occasion, visual direction, comfort, dislikes, movement, coverage, and priorities.",
  };
}

export function evaluateFrontDesignReadiness(
  formState: Partial<CreateFormData>,
  options: { hasFrontImage?: boolean; briefContextText?: string } = {}
): FrontDesignReadiness {
  const briefConfidence = evaluateDesignBriefConfidence(formState, {
    contextText: options.briefContextText,
  });

  if (options.hasFrontImage) {
    return {
      readyToGenerate: false,
      concreteFieldCount: 0,
      promptSignalCount: 0,
      promptSignals: [],
      missingFields: [],
      briefConfidence,
      reason: "Front image already exists.",
    };
  }

  const concreteFields = FRONT_DESIGN_FIELDS.filter(field =>
    hasFieldValue(formState[field])
  );
  const missingFields = FRONT_DESIGN_FIELDS.filter(
    field => !concreteFields.includes(field)
  );
  const promptSignals = getPromptSignals(formState.prompt);
  const uploadedFrontImageUrls = (formState as Record<string, unknown>)
    .uploadedFrontImageUrls;
  const uploadedFrontImageCount = (formState as Record<string, unknown>)
    .uploadedFrontImageCount;
  const hasUploadReference =
    (Array.isArray(formState.uploadedFrontImages) &&
      formState.uploadedFrontImages.length > 0) ||
    (Array.isArray(uploadedFrontImageUrls) &&
      uploadedFrontImageUrls.length > 0) ||
    (typeof uploadedFrontImageCount === "number" &&
      uploadedFrontImageCount > 0);

  if (hasUploadReference) {
    return {
      readyToGenerate: true,
      concreteFieldCount: concreteFields.length,
      promptSignalCount: promptSignals.length,
      promptSignals,
      missingFields,
      briefConfidence,
      reason: "Upload reference provides visual direction.",
    };
  }

  const hasSubstantialPrompt = (formState.prompt?.trim().length ?? 0) >= 80;
  const hasVisualReadiness =
    concreteFields.length >= 2 ||
    promptSignals.length >= 3 ||
    (concreteFields.length >= 1 &&
      promptSignals.length >= 2 &&
      hasSubstantialPrompt);
  const readyToGenerate =
    hasVisualReadiness && briefConfidence.dimensions.visualDirection.captured;

  return {
    readyToGenerate,
    concreteFieldCount: concreteFields.length,
    promptSignalCount: promptSignals.length,
    promptSignals,
    missingFields,
    briefConfidence,
    reason: readyToGenerate
      ? "Enough concrete visual detail has been captured."
      : "Needs at least two concrete design fields or a rich freeform visual description.",
  };
}
